export type Place = {
  id: string;
  name: string;
  center: [number, number];
  place_name: string;
};
export interface IMapState {
  features: Place[];
  query: [number, number];
}

export interface IPlacesPredictionsResponse {
  predictions: google.maps.places.PlacePrediction[];
}

let isPlacesAutocompleteBlocked = false;

const ensurePlacesLibraryLoaded = async (): Promise<void> => {
  if (!window?.google?.maps) return;

  const importLibrary = window.google.maps.importLibrary as undefined | ((libraryName: string) => Promise<unknown>);
  if (importLibrary) {
    await importLibrary('places');
  }
};

const normalizePlaceFields = (fields: string[]): string[] => {
  const mapped = fields.map((f) => {
    switch (f) {
      // Legacy PlacesService field names -> Place class field names
      case 'geometry':
        return 'location';
      case 'formatted_address':
        return 'formattedAddress';
      case 'formatted_phone_number':
        return 'nationalPhoneNumber';
      case 'opening_hours':
        return 'regularOpeningHours';
      case 'address_component':
      case 'address_components':
        return 'addressComponents';
      case 'name':
        return 'displayName';
      default:
        return f;
    }
  });

  // de-dup + keep stable order
  return Array.from(new Set(mapped));
};

const fetchPredictions = async (
  search: string,
  options?: google.maps.places.AutocompleteRequest | google.maps.places.AutocompletionRequest,
): Promise<IPlacesPredictionsResponse> => {
  try {
    if (!window?.google?.maps) return { predictions: [] };

    if (isPlacesAutocompleteBlocked) return { predictions: [] };

    await ensurePlacesLibraryLoaded();

    if (!window.google.maps.places?.AutocompleteSuggestion) return { predictions: [] };

    const request: google.maps.places.AutocompleteRequest = {
      input: search,
      ...(options as google.maps.places.AutocompleteRequest | undefined),
    };

    const { suggestions } =
      await window.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

    const predictions = suggestions
      .map((s) => s.placePrediction)
      .filter((p): p is google.maps.places.PlacePrediction => p !== null);

    return { predictions };
  } catch (e) {
    const message = e instanceof Error ? e.message : String(e);

    // When the key/project doesn't have access to Places (New) endpoint used by
    // AutocompleteSuggestion (AutocompletePlaces), avoid breaking the UX.
    if (
      message.includes('places.googleapis.com') &&
      (message.includes('AutocompletePlaces') || message.toLowerCase().includes('blocked'))
    ) {
      isPlacesAutocompleteBlocked = true;
      console.warn('[Places] AutocompletePlaces blocked (places.googleapis.com).');
      console.warn('[Places] Check API enablement and API key restrictions. Returning empty suggestions.');
      return { predictions: [] };
    }

    console.error(e);
  }

  return { predictions: [] };
};

const fetchLocationByCoords = async (
  location: google.maps.LatLng | null | google.maps.LatLngLiteral,
): Promise<google.maps.GeocoderResponse> => {
  if (!window?.google?.maps) return { results: [], plus_code: undefined };
  try {
    const geocoder = new window.google.maps.Geocoder();
    const locationDetails = await geocoder.geocode({ location }, (locationDetails) => {
      return locationDetails;
    });
    return locationDetails;
  } catch (e) {
    console.error(e);
    return { results: [], plus_code: undefined };
  }
};

// calculate distance , after finish send result to callback function
export const calculateDistance = async (
  origin: google.maps.DistanceMatrixRequest['origins'],
  destination: google.maps.DistanceMatrixRequest['destinations'],
) => {
  const DistanceMatrixService = new google.maps.DistanceMatrixService();
  const response = await DistanceMatrixService.getDistanceMatrix({
    origins: origin,
    destinations: destination,
    travelMode: google.maps.TravelMode.DRIVING,
    // unitSystem: google.maps.UnitSystem.IMPERIAL, // miles and feet.
    unitSystem: google.maps.UnitSystem.METRIC, // kilometers and meters.
  });
  return response;
};

const fetchDetails = async (
  placeId: string,
  fields = ['location', 'formattedAddress', 'nationalPhoneNumber', 'regularOpeningHours'],
): Promise<google.maps.places.Place | null> => {
  try {
    if (!window?.google?.maps) return null;

    await ensurePlacesLibraryLoaded();

    if (!window.google.maps.places?.Place) return null;

    const normalizedFields = normalizePlaceFields(fields);
    const place = new window.google.maps.places.Place({ id: placeId, requestedLanguage: 'es' });
    const { place: fetchedPlace } = await place.fetchFields({ fields: normalizedFields });
    return fetchedPlace;
  } catch (e) {
    console.error(e);
  }

  return null;
};

// https://developers.google.com/maps/documentation/places/web-service/supported_types#table1
const translateType = {
  ATM: 'atm',
  COFFEE: 'cafe',
  GAS: 'gas_station',
  POLICE: 'police',
};

const fetchNearServices = async (
  type: 'ATM' | 'COFFEE' | 'GAS' | 'POLICE',
  center: [number, number],
): Promise<google.maps.places.Place[] | null> => {
  try {
    if (!window?.google?.maps) return null;

    await ensurePlacesLibraryLoaded();

    if (!window.google.maps.places?.Place?.searchNearby) return null;

    const includedType = translateType[type] || 'cafe';

    const { places } = await window.google.maps.places.Place.searchNearby({
      fields: ['id', 'displayName', 'location', 'formattedAddress'],
      includedTypes: [includedType],
      locationRestriction: {
        center: { lat: center[0], lng: center[1] },
        radius: 20_000,
      },
      language: 'es',
      maxResultCount: 20,
    });

    return places;
  } catch (e) {
    console.error(e);
  }
  return null;
};

const getCPFromplaceId = async (placeId: string) => {
  const res = placeId ? await fetchDetails(placeId, ['addressComponents']) : null;
  if (!res?.addressComponents?.length) return '';

  const found = res.addressComponents.find((item) => item.types?.includes('postal_code'));
  return found?.longText || '';
};

export { fetchNearServices, fetchPredictions, fetchDetails, fetchLocationByCoords, getCPFromplaceId };
